import types from '../mutation-types';
import FunnelAPI from '../../api/funnel';
import { dedupeInFlight } from '../utils/dedupeInFlight';

const state = {
  records: [],
  selectedFunnel: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
  stageStats: {}, // Novo: stats agregados por funnelId
};

const getters = {
  getFunnels: $state => $state.records,
  getSelectedFunnel: $state => $state.selectedFunnel,
  getUIFlags: $state => $state.uiFlags,
  getFunnelById: state => id => {
    return state.records.find(funnel => String(funnel.id) === String(id));
  },
  getStageStats: state => funnelId => {
    return state.stageStats[funnelId] || {};
  },
};

const actions = {
  // Sidebar.vue, ChatList.vue and ContactPanel.vue each dispatch this
  // unconditionally on their own mount/init — on a cold open (first inbox
  // navigation of the session) all three can fire in the same tick, so this
  // coalesces them into a single GET /funnels round-trip.
  fetch({ commit, dispatch, state }) {
    return dedupeInFlight('funnel/fetch', async () => {
      commit(types.SET_FUNNEL_UI_FLAG, { isFetching: true });
      try {
        const { data } = await FunnelAPI.get();
        // Guard against poisoned cache payloads (e.g. Hash serialized as
        // [["open", n], ["awaiting_reply", n]]) which would create fake
        // funnels with empty stages and blank the Kanban board.
        if (!Array.isArray(data)) {
          throw new Error('Invalid funnels payload');
        }
        const formattedData = data
          .filter(
            funnel =>
              funnel &&
              typeof funnel === 'object' &&
              !Array.isArray(funnel) &&
              funnel.id != null &&
              funnel.name
          )
          .map(funnel => ({
            ...funnel,
            id: String(funnel.id),
            stages: Object.entries(funnel.stages || {}).reduce(
              (acc, [key, stage]) => ({
                ...acc,
                [String(key)]: {
                  ...(stage && typeof stage === 'object' ? stage : {}),
                  position: parseInt(stage?.position, 10) || 0,
                },
              }),
              {}
            ),
          }));
        commit(types.SET_FUNNELS, formattedData);

        // --- Fallback centralizado ---
        let fallbackFunnel = null;
        if (formattedData.length > 0) {
          // Se já existe um selectedFunnel válido, usa a cópia vinda da API (dados frescos).
          // Não reutilizar state.selectedFunnel — ficaria desatualizado após update (ex.: stage_automations).
          const currentSelectedFunnelId = state.selectedFunnel ? String(state.selectedFunnel.id) : null;
          const freshSelected = formattedData.find(f => String(f.id) === currentSelectedFunnelId);
          if (currentSelectedFunnelId && freshSelected) {
            fallbackFunnel = freshSelected;
          } else {
            fallbackFunnel = formattedData[0];
          }
        }
        await dispatch('setSelectedFunnel', fallbackFunnel);
        // ---
        return formattedData;
      } catch (error) {
        console.error('Erro ao buscar funis:', error);
        // Em caso de erro, limpa o selectedFunnel
        await dispatch('setSelectedFunnel', null);
        throw error;
      } finally {
        commit(types.SET_FUNNEL_UI_FLAG, { isFetching: false });
      }
    });
  },

  async setSelectedFunnel({ commit }, funnel) {
    // Permitir que funnel seja null para desmarcar.
    // Se funnel for inválido, seta null.
    if (!funnel || typeof funnel.id === 'undefined') {
      commit(types.SET_SELECTED_FUNNEL, null);
      return;
    }
    try {
      commit(types.SET_FUNNEL_UI_FLAG, { isUpdating: true });
      commit(types.SET_SELECTED_FUNNEL, funnel); // funnel pode ser null
    } finally {
      commit(types.SET_FUNNEL_UI_FLAG, { isUpdating: false });
    }
  },

  async fetchStageStats({ commit }, { funnelId, filterParams = {} }) {
    try {
      const { data } = await FunnelAPI.getStageStats(funnelId, filterParams);
      commit('SET_STAGE_STATS', { funnelId, stats: data.stages });
      return data.stages;
    } catch (error) {
      console.error('Erro ao buscar stats das etapas:', error);
      commit('SET_STAGE_STATS', { funnelId, stats: {} });
      throw error;
    }
  },
};

const mutations = {
  [types.SET_FUNNELS]($state, data) {
    $state.records = data;
  },
  [types.SET_SELECTED_FUNNEL]($state, funnel) {
    $state.selectedFunnel = funnel;
  },
  [types.SET_FUNNEL_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  SET_STAGE_STATS($state, { funnelId, stats }) {
    $state.stageStats = {
      ...$state.stageStats,
      [funnelId]: stats,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
